// @ts-check
// Module core/data-include
// Support for the data-include attribute. Causes external content to be included inside an
// element that has data-include='some URI'. There is also a data-oninclude attribute that
// features a white space separated list of global methods that will be called with the
// module object, the content, and the included URI.
//
// IMPORTANT:
//  This module only really works when you are in an HTTP context, and will most likely
//  fail if you are editing your documents on your local drive. That is due to security
//  restrictions in the browser.
import { runTransforms, showError } from "./utils.js";
import { markdownToHtml } from "./markdown.js";
import { restructure } from "./sections.js";

export const name = "core/data-include";

/**
 * @param {HTMLElement} el
 * @param {string} data
 * @param {object} options
 * @param {boolean} options.replace
 */
function fillWithText(el, data, { replace }) {
  const { includeFormat } = el.dataset;
  let fill = data;
  if (includeFormat === "markdown") {
    fill = markdownToHtml(fill);
  }

  if (includeFormat === "text") {
    el.textContent = fill;
  } else {
    el.innerHTML = fill;
  }

  if (includeFormat === "markdown") {
    restructure(el);
  }

  if (replace) {
    el.replaceWith(...el.childNodes);
  }
}

/**
 * @param {string} rawData
 * @param {string} id
 * @param {string} url
 */
function processResponse(rawData, id, url) {
  /** @type {HTMLElement | null} */
  const el = document.querySelector(`[data-include-id=${id}]`);
  if (!el) return;
  const data = runTransforms(rawData, el.dataset.oninclude, url);
  const replace = typeof el.dataset.includeReplace === "string";
  fillWithText(el, data, { replace });
  // If still in the dom tree, clean up
  if (!replace) {
    removeIncludeAttributes(el);
  }
}
/**
 * Removes attributes after they are used for inclusion, if present.
 *
 * @param {Element} el The element to clean up.
 */
function removeIncludeAttributes(el) {
  [
    "data-include",
    "data-include-format",
    "data-include-replace",
    "data-include-id",
    "data-oninclude",
  ].forEach(attr => el.removeAttribute(attr));
}

export async function run() {
  await runIncludes(document, 1);
}

/**
 * @param {HTMLElement | Document} root
 * @param {number} currentDepth
 */
async function runIncludes(root, currentDepth) {
  /** @type {NodeListOf<HTMLElement>} */
  const includables = root.querySelectorAll("[data-include]");
  const promisesToInclude = Array.from(includables).map(async el => {
    const url = el.dataset.include;
    if (!url) {
      return; // just skip it
    }
    const id = `include-${String(Math.random()).slice(2)}`;
    el.dataset.includeId = id;
    try {
      const response = await fetch(url);
      const text = await response.text();
      processResponse(text, id, url);
      if (currentDepth < 3) {
        // For performance reasons, only allow limited nesting.
        await runIncludes(el, currentDepth + 1);
      }
    } catch (e) {
      const err = /** @type {Error} */ (e);
      const msg = `\`data-include\` failed: \`${url}\` (${err.message}).`;
      showError(msg, name, { elements: [el], cause: err });
    }
  });
  await Promise.all(promisesToInclude);
}
